import {
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useWorkspaceKeys, type WorkspaceKeys } from "../../app/current-user.tsx";
import { researchApi } from "../../client.ts";
import type {
  CreateResearchInput,
  RescoreResearchInput,
  ResearchDetail,
  ResearchSummary,
  UpdateResearchInput,
} from "../../domain/schema.ts";
import type { WorkspaceWrite } from "../../lib/workspace-cache.ts";
import { useWorkspaceMutation } from "../../lib/workspace-mutation.ts";
import { workspaceQuery, type WorkspaceQuery } from "../../lib/workspace-query.ts";

export const researchListQuery = (keys: WorkspaceKeys): WorkspaceQuery<ResearchSummary[]> =>
  workspaceQuery(keys.researchList(), () => researchApi.listResearch());

export const researchQuery = (keys: WorkspaceKeys, id: string): WorkspaceQuery<ResearchDetail> =>
  workspaceQuery(keys.research(id), () => researchApi.getResearch(id));

export const useResearchList = (): UseQueryResult<ResearchSummary[]> =>
  useQuery(researchListQuery(useWorkspaceKeys()));

export const useResearch = (id: string): UseQueryResult<ResearchDetail> =>
  useQuery(researchQuery(useWorkspaceKeys(), id));

/** Fetches the research again (also when cached), e.g. to reset an editor after a 409 STALE. */
export const useReloadResearch = (id: string): (() => Promise<ResearchDetail>) => {
  const queryClient = useQueryClient();
  const keys = useWorkspaceKeys();
  return () => queryClient.query({ ...researchQuery(keys, id), staleTime: 0 });
};

/** Every research write answers the research as its page shows it. */
const writeResearch = (keys: WorkspaceKeys, detail: ResearchDetail): WorkspaceWrite => ({
  set: [[keys.research(detail.id), detail]],
});

/** Analyst: start the research of a sent or received sample (a draft with lab defaults). */
export const useCreateResearch = (): UseMutationResult<
  ResearchDetail,
  Error,
  CreateResearchInput
> =>
  useWorkspaceMutation({
    mutationFn: (input: CreateResearchInput) => researchApi.admin.createResearch(input),
    cache: writeResearch,
  });

export const useUpdateResearch = (
  id: string,
): UseMutationResult<ResearchDetail, Error, UpdateResearchInput> =>
  useWorkspaceMutation({
    mutationFn: (input: UpdateResearchInput) => researchApi.admin.updateResearch(id, input),
    cache: writeResearch,
  });

/** Analyst: recompute the results from the stored input with the current method. */
export const useRescoreResearch = (
  id: string,
): UseMutationResult<ResearchDetail, Error, RescoreResearchInput> =>
  useWorkspaceMutation({
    mutationFn: (input: RescoreResearchInput) => researchApi.admin.rescoreResearch(id, input),
    cache: writeResearch,
  });

export const useDeleteResearch = (): UseMutationResult<void, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.admin.deleteResearch(id),
    cache: (keys, _data, id) => ({ remove: [keys.research(id)] }),
  });
